<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { Switch } from '@/components/ui/switch'
import { toast } from '@/components/ui/toast'
import {
  fetchFeatureMonitoring,
  enableFeatureMonitoring,
  disableFeatureMonitoring,
} from '@/services/monitoringApi'
import { calculateArea, MAX_MONITORING_AREA_SQKM } from '@/utils/geometry'

const props = defineProps({
  feature: {
    type: Object,
    required: true,
  },
})

const store = useStore()

const orgId = computed(() => store.getters['org/getSelectedOrg']?.organization_id)
const projectId = computed(() => props.feature.properties.project_id)
const featureCode = computed(() => props.feature.properties.code)

const monitored = ref(false)
const loaded = ref(false)
const busy = ref(false)

const overLimit = computed(() =>
  props.feature.geometry ? calculateArea(props.feature.geometry) / 1e6 > MAX_MONITORING_AREA_SQKM : false)

const load = async () => {
  if (!orgId.value || !projectId.value || loaded.value) return
  try {
    const state = await fetchFeatureMonitoring(orgId.value, projectId.value, featureCode.value)
    monitored.value = !!state.monitored
    loaded.value = true
  } catch (e) {
    loaded.value = false
  }
}

const toggle = async (value) => {
  if (busy.value) return
  busy.value = true
  monitored.value = value
  try {
    if (value) {
      await enableFeatureMonitoring(orgId.value, projectId.value, featureCode.value)
    } else {
      await disableFeatureMonitoring(orgId.value, projectId.value, featureCode.value)
    }
  } catch (error) {
    monitored.value = !value
    toast({
      title: value ? 'Could not enable monitoring' : 'Could not disable monitoring',
      description: error.response?.data?.detail || error.message,
      variant: 'destructive',
    })
  } finally {
    busy.value = false
  }
}

watch(orgId, load, { immediate: true })
</script>

<template>
  <div v-if="loaded" class="mb-4">
    <h3 class="mb-1 text-base font-bold">Monitoring</h3>
    <div class="flex items-center">
      <Switch
        :model-value="monitored"
        :disabled="busy || (overLimit && !monitored)"
        @update:model-value="toggle"
      />
      <span class="ml-2 text-muted-foreground">Monitor area</span>
    </div>
    <p v-if="overLimit && !monitored" class="mt-1 text-sm text-muted-foreground">
      Monitoring areas are limited to {{ MAX_MONITORING_AREA_SQKM }} km².
    </p>
  </div>
</template>
